import { TappIcon } from '@/components/ui/TappIcon';
import { TappPasswordInput } from '@/components/ui/TappPasswordInput';
import { Button } from '@/components/ui/button';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import AppError from '@/components/AppError';
import { useAuth } from '@/lib/auth.store';
import { useTranslation } from 'react-i18next';

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const [error, setError] = useState<any>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();

  const token = searchParams.get('token');

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm({
    resolver: zodResolver(
      z
        .object({
          newPassword: z.string().min(6, t('resetPasswordMinLength')),
          confirmPassword: z.string(),
        })
        .refine((data) => data.newPassword === data.confirmPassword, {
          path: ['confirmPassword'],
          message: t('resetPasswordMismatch'),
        })
    ),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: any) => {
    try {
      setError(null);
      setSuccess(null);

      if (!token) {
        setError(new Error(t('resetPasswordMissingToken')));
        return;
      }

      const res = await auth.resetPassword(token, data.newPassword);
      setSuccess(res.message);

      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <main className="flex flex-col items-center justify-center px-2 py-16">
      <div className="mb-6 h-14 w-14">
        <img src="/logo_small.png" alt="Logo" />
      </div>

      <h1 className="mb-6 text-center text-2xl font-semibold tracking-tight">{t('resetPasswordTitle')}</h1>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full max-w-xs flex-1 flex-col items-center justify-center"
      >
        <Controller
          name="newPassword"
          control={control}
          render={({ field, fieldState }) => (
            <TappPasswordInput
              className="mb-2 w-full"
              border
              placeholder={t('resetPasswordNewPlaceholder')}
              prefix={<TappIcon>lock</TappIcon>}
              {...field}
              {...fieldState}
            />
          )}
        />

        <Controller
          name="confirmPassword"
          control={control}
          render={({ field, fieldState }) => (
            <TappPasswordInput
              className="mb-4 w-full"
              border
              placeholder={t('resetPasswordConfirmPlaceholder')}
              prefix={<TappIcon>lock</TappIcon>}
              {...field}
              {...fieldState}
            />
          )}
        />

        <Button className="w-full" type="submit" disabled={isSubmitting}>
          {t('resetPasswordButton')}
        </Button>

        <AppError error={error} className="mt-4" />
        {success && <p className="mt-4 text-sm text-green-600 text-center">{success}</p>}
      </form>
    </main>
  );
};

export default ResetPasswordPage;
